import { ReactNode, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Link, SvgIcon, Tooltip, Typography } from "@mui/material";
import { ArrowPopover, CoreIconButton } from "@verity/components/core";
import LightBulbIcon from "../../../images/portal/light-bulb.svg?react";
import VideoIcon from "../../../images/portal/video.svg?react";
import PopoutIcon from "../../../images/portal-navigation/popoutIcon.svg?react";
import CloseIcon from "../../../images/portal/cross-x.svg?react";
import styles from "./index.module.css";

type VideoPopoverProps = {
	src: string;
	videoTitle?: string;
	linkText: string;
	header?: ReactNode;
	description?: ReactNode;
};

export default function VideoPopover({ src, videoTitle, linkText, header, description }: VideoPopoverProps) {
	const { i18n } = useTranslation();
	const [open, setOpen] = useState(false);
	const linkRef = useRef<HTMLDivElement>(null);
	const headerId = useId();
	const closePopover = () => setOpen(false);

	return (
		<div className={styles.videoLink}>
			<div className={styles.videoLinkText}>
				<LightBulbIcon />
				<Typography>
					<strong>{i18n.t("portal.components.videoPopoverTSX.howTo")}</strong>
					{linkText}
				</Typography>
			</div>
			<div ref={linkRef} className={styles.videoPopoverLinkContainer}>
				<Link
					component="button"
					aria-haspopup="dialog"
					aria-expanded={open}
					onClick={() => setOpen(true)}
					className={styles.videoPopoverLink}
					color="#0B6F85"
				>
					<VideoIcon />
					{i18n.t("portal.components.videoPopoverTSX.watchVideo")}
				</Link>
			</div>
			<ArrowPopover
				open={open}
				onClose={closePopover}
				anchorEl={linkRef.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				slotProps={{
					paper: {
						role: "dialog",
						"aria-modal": "true",
						"aria-labelledby": headerId,
						sx: {
							width: "100%",
							maxWidth: { xs: "calc(100vw - 64px)", sm: "450px" },
							overflow: "visible",
							border: "1.5px solid #e4e4e4",
							borderRadius: "7px",
							marginTop: 1.5,
						},
					},
				}}
			>
				{(header || description) && (
					<div className={styles.popoverHeader}>
						{header && (
							<div className={styles.header}>
								<Typography id={headerId} component="h2" fontSize="15px" fontWeight="500" color="#42296e">
									{header}
								</Typography>
								<div className={styles.headerButtons}>
									<Tooltip title={i18n.t("portal.components.videoPopoverTSX.popoutButtonAriaLabel")}>
										<IconButton
											component="a"
											href={src}
											target="_blank"
											rel="noopener noreferrer"
											disableRipple
											sx={{
												minWidth: 0,
												padding: 0,
												"&:focus-visible": {
													outline: "2px solid var(--focus-outline-color-dark)",
													borderRadius: "2px",
												},
											}}
										>
											<SvgIcon>
												<PopoutIcon />
											</SvgIcon>
										</IconButton>
									</Tooltip>
									<Tooltip title={i18n.t("portal.components.videoPopoverTSX.closeButtonAriaLabel")}>
										<CoreIconButton variant="plain" onClick={closePopover} sx={{ paddingBottom: "0px" }}>
											<SvgIcon>
												<CloseIcon />
											</SvgIcon>
										</CoreIconButton>
									</Tooltip>
								</div>
							</div>
						)}
						{description && (
							<div className={styles.description}>
								<Typography fontSize="12px" fontWeight="400">
									{description}
								</Typography>
							</div>
						)}
					</div>
				)}
				<iframe src={src} title={videoTitle} className={styles.video} allowFullScreen loading="lazy" />
			</ArrowPopover>
		</div>
	);
}
